/**
 * The search box and the weight filter.
 *
 * Two controls over the same query, because `findYarns` takes free text and an exact
 * weight and the two narrow together. That pairing is the reason the weight sits in a
 * row of chips rather than in a select: a knitter browsing a shop wants to see that
 * there are three DKs and one aran before choosing, and a closed select hides exactly
 * that.
 *
 * The counts on the chips are the counts under the current text query, not the counts
 * in the whole catalogue, so a chip that would return nothing says so before it is
 * pressed rather than after.
 *
 * Nothing here submits. There is no server, the filter is synchronous, and a form
 * that reloaded the page would only lose the query. The `<form>` element stays because
 * a search field belongs in one and `role="search"` is how a screen reader finds it.
 */

import { YARNS, type YarnWeight } from '../catalogue';
import { SORT_ORDERS, isSortKey, type SortKey } from '../sort';

/** The weights in the order a spinner would list them, finest first. */
export const WEIGHT_ORDER: ReadonlyArray<YarnWeight> = Object.freeze([
  'fingering',
  'sport',
  'dk',
  'worsted',
  'aran',
]);

/** How a weight word is set in the chips. `dk` is initials, the rest are words. */
export const weightLabel = (weight: YarnWeight): string =>
  weight === 'dk' ? 'DK' : weight;

export interface BrowseBarProps {
  readonly query: string;
  readonly onQuery: (query: string) => void;
  /** The weight being filtered on, or null for every weight. */
  readonly weight: YarnWeight | null;
  readonly onWeight: (weight: YarnWeight | null) => void;
  /** How many yarns each chip would leave, under the current text query. */
  readonly counts: Readonly<Record<string, number>>;
  readonly shown: number;
  /** Which order the shelf is in. See `sort.ts`. */
  readonly sort: SortKey;
  readonly onSort: (sort: SortKey) => void;
}

export const BrowseBar = ({
  query,
  onQuery,
  weight,
  onWeight,
  counts,
  shown,
  sort,
  onSort,
}: BrowseBarProps): JSX.Element => (
  <form
    className="browse"
    data-region="browse"
    onSubmit={(event) => event.preventDefault()}
    role="search"
  >
    <div className="browse__field">
      <label className="eyebrow browse__label" htmlFor="q">
        Search the stock
      </label>
      <input
        autoComplete="off"
        className="browse__input"
        id="q"
        name="q"
        onChange={(event) => onQuery(event.target.value)}
        placeholder="worsted wool, alpaca, peruvian"
        type="search"
        value={query}
      />
    </div>

    <div className="browse__weights" role="group" aria-label="Filter by weight">
      <button
        aria-pressed={weight === null}
        className="chip"
        onClick={() => onWeight(null)}
        type="button"
      >
        All
        <span className="chip__count num">{counts['all'] ?? 0}</span>
      </button>

      {WEIGHT_ORDER.map((word) => (
        <button
          aria-pressed={weight === word}
          className="chip"
          data-weight={word}
          disabled={(counts[word] ?? 0) === 0}
          key={word}
          onClick={() => onWeight(weight === word ? null : word)}
          type="button"
        >
          {weightLabel(word)}
          <span className="chip__count num">{counts[word] ?? 0}</span>
        </button>
      ))}
    </div>

    {/*
      A native select rather than another row of chips. The four orders are mutually
      exclusive and only one can be true at a time, which is what a select is for, and
      five weight chips plus four order chips in one bar would be nine things that look
      alike and mean two different kinds of thing.
    */}
    <div className="browse__field browse__field--sort">
      <label className="eyebrow browse__label" htmlFor="sort">
        Order
      </label>
      <span className="browse__select">
        <select
          className="browse__sort"
          id="sort"
          name="sort"
          onChange={(event) => {
            const chosen = event.target.value;
            if (isSortKey(chosen)) onSort(chosen);
          }}
          value={sort}
        >
          {SORT_ORDERS.map((order) => (
            <option key={order.key} value={order.key}>
              {order.label}
            </option>
          ))}
        </select>
        <span aria-hidden="true" className="browse__arrow" />
      </span>
    </div>

    <p className="browse__count" role="status">
      Showing <span className="num">{shown}</span> of{' '}
      <span className="num">{YARNS.length}</span>
    </p>
  </form>
);
